import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { flyers, branding as brandingApi } from '../utils/api';
import { 
  FileImage, 
  Download, 
  Palette, 
  ArrowRight, 
  Sparkles,
  AlertCircle
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const [recentFlyers, setRecentFlyers] = useState([]);
  const [flyerCount, setFlyerCount] = useState(0);
  const [brandingStatus, setBrandingStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [flyersRes, brandingRes] = await Promise.all([
          flyers.getAll(),
          brandingApi.get()
        ]);
        setFlyerCount(flyersRes.data.flyers.length);
        setRecentFlyers(flyersRes.data.flyers.slice(0, 4));
        setBrandingStatus(brandingRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const brandingComplete = brandingStatus?.onboardingComplete === true;
  const needsBranding = !brandingComplete;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 font-display">
          Welcome back, {user?.contactName || user?.companyName}! 👋
        </h1>
        <p className="text-gray-600 mt-2">
          Ready to create some great marketing materials?
        </p>
      </div>

      {/* Branding Alert */}
      {needsBranding && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 mb-8 animate-fade-in">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertCircle className="text-amber-600" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800">Complete Your Branding Setup</h3>
              <p className="text-amber-700 text-sm mt-1">
                Upload your logo or use a company-name text logo, then set your brand colors to customize every flyer.
              </p>
              <Link 
                to="/branding" 
                className="inline-flex items-center gap-2 mt-3 text-amber-700 hover:text-amber-800 font-semibold text-sm"
              >
                Set Up Branding <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center">
              <FileImage className="text-primary-500" size={28} />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">{flyerCount}</p>
              <p className="text-gray-500 text-sm">Available Flyers</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-secondary-100 rounded-xl flex items-center justify-center">
              <Download className="text-secondary-500" size={28} />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">4</p>
              <p className="text-gray-500 text-sm">Size Options</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
              <Palette className="text-green-500" size={28} />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">
                {brandingComplete ? '✓' : '○'}
              </p>
              <p className="text-gray-500 text-sm">
                {brandingComplete ? 'Branding Complete' : 'Setup Needed'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Flyers */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Latest Flyers</h2>
          <Link 
            to="/flyers" 
            className="text-primary-500 hover:text-primary-600 font-semibold text-sm flex items-center gap-1"
          >
            View All <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {recentFlyers.map((flyer) => (
            <Link key={flyer.id} to={`/flyers/${flyer.id}`} className="flyer-card">
              <div className="aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center">
                {flyer.thumbnailUrl ? (
                  <img 
                    src={flyer.thumbnailUrl} 
                    alt={flyer.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center p-4">
                    <FileImage className="mx-auto text-gray-300 mb-2" size={48} />
                    <p className="text-xs text-gray-400">Preview</p>
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-800 truncate">{flyer.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{flyer.category}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link 
          to="/flyers" 
          className="card p-6 hover:border-primary-300 group"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center group-hover:bg-primary-500 transition-colors">
              <Sparkles className="text-primary-500 group-hover:text-white transition-colors" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 group-hover:text-primary-500 transition-colors">
                Browse All Flyers
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Explore our complete library of marketing materials
              </p>
            </div>
          </div>
        </Link>

        <Link 
          to="/branding" 
          className="card p-6 hover:border-secondary-300 group"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center group-hover:bg-secondary-500 transition-colors">
              <Palette className="text-secondary-500 group-hover:text-white transition-colors" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 group-hover:text-secondary-500 transition-colors">
                Customize Branding
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Update your logo and brand colors
              </p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
